import { useMemo } from "react";

import { parse } from "date-fns";
import isNil from "lodash/isNil";
import noop from "lodash/noop";

import { DateFormat } from "@/constants/DateFormat";
import { ProfileTestID } from "@/constants/TestID";
import DateTimePicker from "@/ui/composed/pickers/DateTimePicker";
import MultiSelectTextField from "@/ui/composed/pickers/MultiSelectTextField";
import { LabeledInput } from "@/ui/core";
import { formatDate } from "@/utils/date.util";
import type { PermissionChecker } from "@/utils/profile/permissions.util";
import { isEditable, isViewable } from "@/utils/profile/permissions.util";
import { isNotNilNotEmpty } from "@/utils/shared.utils";
import { joinTestIDs } from "@/utils/string.util";

const baseTestID = ProfileTestID.OtherInfo;

const DateOfBirthField = ({
  dateOfBirth,
  setDateOfBirth,
  trackFieldPressIn,
  userPermissions,
}: {
  dateOfBirth?: string;
  setDateOfBirth: (value: string) => void;
  trackFieldPressIn?: (fieldName: string) => void;
  userPermissions: PermissionChecker;
}) => {
  return useMemo(() => {
    if (!isViewable(userPermissions("dateOfBirth"))) {
      return null;
    }
    return (
      <LabeledInput
        leftText="Date of birth"
        testID={joinTestIDs(baseTestID, "dateOfBirth-label")}
      >
        <DateTimePicker
          date={
            isNil(dateOfBirth)
              ? undefined
              : parse(dateOfBirth, DateFormat.yearMonthDay, new Date())
          }
          isDisabled={!isEditable(userPermissions("dateOfBirth"))}
          mode="date"
          onChange={(value) => {
            const dateString = formatDate({
              date: value,
              format: DateFormat.yearMonthDay,
            });
            setDateOfBirth(dateString);
            trackFieldPressIn?.(DateOfBirthField.name);
          }}
          testID={joinTestIDs(baseTestID, "dateOfbirth")}
        />
      </LabeledInput>
    );
  }, [dateOfBirth, setDateOfBirth, userPermissions, trackFieldPressIn]);
};

const RelationToPatientField = ({
  options,
  patientName = "patient(s)",
  relationToPatient,
  setRelationToPatient,
  trackFieldPressIn,
  userPermissions,
}: {
  options: MultiSelectOption[];
  patientName?: string;
  relationToPatient?: string;
  setRelationToPatient: (value: string) => void;
  trackFieldPressIn?: (fieldName: string) => void;
  userPermissions: PermissionChecker;
}) => {
  return useMemo(() => {
    if (
      !isViewable(userPermissions("relationToPatient")) ||
      isNil(relationToPatient)
    ) {
      return null;
    }

    const defaultSelectedValues = [
      { displayText: relationToPatient, value: relationToPatient },
    ];

    return (
      <LabeledInput
        leftText={`Relationship to ${patientName}`}
        testID={joinTestIDs(baseTestID, "relationToPatient-label")}
      >
        <MultiSelectTextField
          defaultSelectedValues={defaultSelectedValues}
          isDisabled={!isEditable(userPermissions("relationToPatient"))}
          isSingleSelect
          modalTitle={`Relationship to ${patientName}`}
          onPressIn={() => trackFieldPressIn?.(RelationToPatientField.name)}
          onValuesChange={(selection) =>
            setRelationToPatient(selection.length > 0 ? selection[0].value : "")
          }
          options={options}
          testID={joinTestIDs(baseTestID, "relationToPatient")}
          value={relationToPatient}
        />
      </LabeledInput>
    );
  }, [
    options,
    patientName,
    relationToPatient,
    setRelationToPatient,
    userPermissions,
    trackFieldPressIn,
  ]);
};

const SupportedByField = ({
  supports,
  userPermissions,
}: {
  supports?: string[];
  userPermissions: PermissionChecker;
}) => {
  return useMemo(() => {
    if (
      !isViewable(userPermissions("supportedBy")) ||
      !isNotNilNotEmpty(supports)
    ) {
      return null;
    }
    const supportText = supports.join(",\n");

    return (
      <LabeledInput
        leftText="Supported by"
        testID={joinTestIDs(
          baseTestID,
          "relationToPatient-label",
          "supportedBy",
        )}
      >
        <MultiSelectTextField
          isDisabled
          isSingleSelect
          modalTitle="Supported by"
          onPressIn={noop}
          onValuesChange={noop}
          options={[]}
          testID={joinTestIDs(baseTestID, "relationToPatient", "supportedBy")}
          value={supportText}
        />
      </LabeledInput>
    );
  }, [supports, userPermissions]);
};

const LanguagesField = ({
  languages,
  options,
  setLanguages,
  trackFieldPressIn,
  userPermissions,
}: {
  languages?: string[];
  options: MultiSelectOption[];
  setLanguages: (value: string[]) => void;
  trackFieldPressIn?: (fieldName: string) => void;
  userPermissions: PermissionChecker;
}) => {
  return useMemo(() => {
    if (!isViewable(userPermissions("languages"))) {
      return null;
    }

    const defaultSelectedValues = isNil(languages)
      ? []
      : languages.map((language: string) => ({
          displayText: language,
          value: language,
        }));

    return (
      <LabeledInput
        leftText="Languages spoken"
        testID={joinTestIDs(baseTestID, "languages-label")}
      >
        <MultiSelectTextField
          defaultSelectedValues={defaultSelectedValues}
          isDisabled={!isEditable(userPermissions("languages"))}
          modalTitle="Languages spoken"
          onPressIn={() => trackFieldPressIn?.(LanguagesField.name)}
          onValuesChange={(selections) =>
            setLanguages(
              selections.map((option: MultiSelectOption) => option.value),
            )
          }
          options={options}
          testID={joinTestIDs(baseTestID, "languages")}
          value={languages}
        />
      </LabeledInput>
    );
  }, [options, languages, setLanguages, userPermissions, trackFieldPressIn]);
};

export {
  DateOfBirthField,
  LanguagesField,
  RelationToPatientField,
  SupportedByField,
};
